import type { User, RewardTaskType } from '@/types';
import { TASK_REWARD_CREDITS } from '@/types';

/**
 * Whether the Rewards section should be visible.
 * Shown to every free-plan user in their PERSONAL workspace. Paid users have
 * nothing to gain, and rewards are personal — hidden while a Team workspace
 * (lens) is active.
 */
export const canSeeRewards = (
  user?: User | null,
  activeOrgId?: string | null,
): boolean => {
  if (!user) return false;
  if (activeOrgId) return false;
  const plan = user.userPlan ?? 'free';
  return plan === 'free';
};

export interface RewardTaskOrder {
  type: RewardTaskType;
  rewardCredits: number;
}

/** Canonical store / GPT links — keep in sync with landing Footer.tsx. */
export const REWARD_LINKS = {
  appStore: 'https://apps.apple.com/us/app/vivideo-ai-ai-video-generator/id6755797079',
  googlePlay: 'https://play.google.com/store/apps/details?id=com.vivideo.ai',
  chromeExtension:
    'https://chromewebstore.google.com/detail/vivideo-ai-video-generato/ednbjnaokgnpjfmihidkhmopelpppjmf',
  chatGptGpt:
    'https://chatgpt.com/g/g-6920d74f5328819180b993e3976d7f1e-ai-video-generator-vivideo',
  // Google "Preferred sources" picker pre-scoped to vivideo.ai — the user just
  // adds it and screenshots the result.
  googlePreferredSource: 'https://www.google.com/preferences/source?q=vivideo.ai',
  // "Evaluate" opens Trustpilot's write-a-review flow directly.
  trustpilotWrite: 'https://www.trustpilot.com/evaluate/vivideo.ai',
  trustpilotListing: 'https://www.trustpilot.com/review/vivideo.ai',
  site: 'https://vivideo.ai',
  app: 'https://app.vivideo.ai',
} as const;

export const APP_STORE_ID = '6755797079';
export const PLAY_PACKAGE_ID = 'com.vivideo.vivideo_mobile';
export const CHROME_EXTENSION_ID = 'ednbjnaokgnpjfmihidkhmopelpppjmf';
export const CHATGPT_GPT_ID = 'g-6920d74f5328819180b993e3976d7f1e';

/**
 * Two high-value tasks pinned to the TOP of the list, in this fixed order, for
 * every user: #1 the hero Trustpilot 5-star review (worth 5 credits — see
 * `TASK_REWARD_CREDITS`), #2 make vivideo.ai a Google preferred source.
 */
const PINNED_REWARD_TASKS: ReadonlyArray<RewardTaskType> = [
  'review_trustpilot',
  'google_preferred_source',
];

/** The remaining tasks, shuffled per-user beneath the pinned two. */
const ALL_REWARD_TASKS: ReadonlyArray<RewardTaskType> = [
  'share_x',
  'share_reddit',
  'share_linkedin',
  'share_facebook',
  'share_pinterest',
  'review_ios',
  'review_android',
  'review_chrome',
  'review_chatgpt',
];

/**
 * A/B: the lower half of the 16 ab groups ([0,7]) sees a single Trustpilot
 * review task worth 5 credits instead of the full task catalog.
 */
export function isTrustpilotOnlyRewards(abGroup?: number | null): boolean {
  return (
    typeof abGroup === 'number' &&
    Number.isFinite(abGroup) &&
    abGroup >= 0 &&
    abGroup <= 7
  );
}

/**
 * A/B: even ab groups (0,2,…,14) get locale-prefixed landing links in share
 * tasks (e.g. vivideo.ai/tr). Odd groups always share the English root.
 */
export function usesLocalizedShareLinks(abGroup?: number | null): boolean {
  return typeof abGroup === 'number' && Number.isFinite(abGroup) && abGroup % 2 === 0;
}

/**
 * Landing homepage URL for shares. English (and missing locale) → vivideo.ai;
 * otherwise vivideo.ai/{lang} when the user's AB cohort is in the localized half.
 */
export function getShareSiteUrl(
  language?: string | null,
  abGroup?: number | null,
): string {
  if (!usesLocalizedShareLinks(abGroup)) return REWARD_LINKS.site;
  const lang = (language || 'en').toLowerCase().split('-')[0];
  if (!lang || lang === 'en') return REWARD_LINKS.site;
  return `${REWARD_LINKS.site}/${lang}`;
}

/**
 * Tasks that only need a screenshot as proof (no proof URL). The Trustpilot
 * 5-star review is screenshot-only to reduce friction on the hero task.
 */
const SCREENSHOT_ONLY_TASKS: ReadonlySet<RewardTaskType> = new Set([
  'review_trustpilot',
]);

/** Whether a task requires the user to paste a proof URL (vs. screenshot only). */
export function taskRequiresProofUrl(type: RewardTaskType): boolean {
  return !SCREENSHOT_ONLY_TASKS.has(type);
}

export interface TaskCtaOptions {
  language?: string | null;
  abGroup?: number | null;
}

/** Primary CTA URL for a task (open Google / store / compose). */
export function getTaskCtaUrl(type: RewardTaskType, options: TaskCtaOptions = {}): string {
  const siteUrl = getShareSiteUrl(options.language, options.abGroup);
  switch (type) {
    case 'google_preferred_source':
      // Direct Preferred-sources link scoped to vivideo.ai — add it, screenshot.
      return REWARD_LINKS.googlePreferredSource;
    case 'share_x':
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `I've been creating AI videos with Vivideo — try it free: ${siteUrl}`,
      )}`;
    case 'share_reddit':
      return `https://www.reddit.com/submit?url=${encodeURIComponent(siteUrl)}&title=${encodeURIComponent('Vivideo — free AI video generator')}`;
    case 'share_linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(siteUrl)}`;
    case 'share_facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}`;
    case 'share_pinterest':
      return `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(siteUrl)}&description=${encodeURIComponent('Vivideo — AI video generator')}`;
    case 'review_ios':
      return REWARD_LINKS.appStore;
    case 'review_android':
      return REWARD_LINKS.googlePlay;
    case 'review_chrome':
      return REWARD_LINKS.chromeExtension;
    case 'review_chatgpt':
      return REWARD_LINKS.chatGptGpt;
    case 'review_trustpilot':
      return REWARD_LINKS.trustpilotWrite;
  }
}

function isGoogleHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === 'google.com' || h.endsWith('.google.com') || /^([a-z0-9-]+\.)*google\.[a-z.]+$/.test(h);
}

/**
 * Validate proof_url for a task type. Mirrors backend submit-review rules.
 * Returns null if valid, otherwise an error message key suffix or plain message.
 */
export function validateProofUrl(
  taskType: RewardTaskType,
  proofUrl: string,
): string | null {
  const raw = (proofUrl || '').trim();
  if (!raw) return taskRequiresProofUrl(taskType) ? 'proofUrlRequired' : null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return 'proofUrlInvalid';
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return 'proofUrlInvalid';
  }

  const host = url.hostname.toLowerCase();
  const path = url.pathname;
  const pathLower = path.toLowerCase();

  if (taskType === 'google_preferred_source') {
    // Proof is really the screenshot (manual review); just require a Google URL.
    if (!isGoogleHost(host)) return 'proofUrlGooglePreferred';
    return null;
  }

  if (taskType === 'share_x') {
    if (!['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com'].includes(host)) {
      return 'proofUrlX';
    }
    if (!/\/status\/\d+/i.test(path)) return 'proofUrlXStatus';
    return null;
  }

  if (taskType === 'share_reddit') {
    if (!['reddit.com', 'www.reddit.com', 'old.reddit.com', 'new.reddit.com'].includes(host)) {
      return 'proofUrlReddit';
    }
    // Post or comment: /r/.../comments/...
    if (!/\/r\/[^/]+\/comments\//i.test(path) && !/\/comments\//i.test(path)) {
      return 'proofUrlRedditPost';
    }
    return null;
  }

  if (taskType === 'share_linkedin') {
    if (!host.endsWith('linkedin.com')) return 'proofUrlLinkedin';
    return null;
  }

  if (taskType === 'share_facebook') {
    if (
      !['facebook.com', 'www.facebook.com', 'm.facebook.com', 'fb.com', 'www.fb.com', 'fb.watch'].includes(
        host,
      ) &&
      !host.endsWith('.facebook.com')
    ) {
      return 'proofUrlFacebook';
    }
    return null;
  }

  if (taskType === 'share_pinterest') {
    if (
      !['pinterest.com', 'www.pinterest.com', 'pin.it'].includes(host) &&
      !host.endsWith('.pinterest.com')
    ) {
      return 'proofUrlPinterest';
    }
    return null;
  }

  if (taskType === 'review_ios') {
    if (!host.endsWith('apps.apple.com') || !path.includes(APP_STORE_ID)) {
      return 'proofUrlIos';
    }
    return null;
  }

  if (taskType === 'review_android') {
    if (!host.includes('play.google.com')) return 'proofUrlAndroid';
    const id = url.searchParams.get('id') || '';
    if (id !== PLAY_PACKAGE_ID && !raw.includes(PLAY_PACKAGE_ID)) {
      return 'proofUrlAndroid';
    }
    return null;
  }

  if (taskType === 'review_chrome') {
    if (!host.includes('chromewebstore.google.com') || !path.includes(CHROME_EXTENSION_ID)) {
      return 'proofUrlChrome';
    }
    return null;
  }

  if (taskType === 'review_chatgpt') {
    if (!['chatgpt.com', 'www.chatgpt.com', 'chat.openai.com'].includes(host)) {
      return 'proofUrlChatgpt';
    }
    if (!path.includes(CHATGPT_GPT_ID)) return 'proofUrlChatgpt';
    return null;
  }

  if (taskType === 'review_trustpilot') {
    if (host !== 'trustpilot.com' && !host.endsWith('.trustpilot.com')) {
      return 'proofUrlTrustpilot';
    }
    if (!pathLower.includes('vivideo.ai')) return 'proofUrlTrustpilot';
    return null;
  }

  return 'proofUrlInvalid';
}

/** FNV-1a 32-bit hash — stable across sessions for the same seed string. */
function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mulberry32 PRNG — deterministic shuffle from a numeric seed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Reward tasks for a user: the two PINNED tasks first (Trustpilot 5-star review,
 * then Google preferred source — fixed order for everyone), followed by the remaining tasks
 * in a per-user random order (stable for a given userId). Credit amounts come
 * from `TASK_REWARD_CREDITS` — keep in sync with the backend `submit-review`.
 *
 * The short-list AB cohort ([0,7]) sees only the two pinned tasks.
 */
export const getRewardTaskOrder = (
  userId: string,
  abGroup?: number | null,
): RewardTaskOrder[] => {
  const pinned: RewardTaskOrder[] = PINNED_REWARD_TASKS.map((type) => ({
    type,
    rewardCredits: TASK_REWARD_CREDITS[type],
  }));

  if (isTrustpilotOnlyRewards(abGroup)) {
    return pinned;
  }

  const tasks = [...ALL_REWARD_TASKS];
  const rand = mulberry32(hashSeed(userId || 'anonymous'));
  for (let i = tasks.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [tasks[i], tasks[j]] = [tasks[j], tasks[i]];
  }
  return [
    ...pinned,
    ...tasks.map((type) => ({
      type,
      rewardCredits: TASK_REWARD_CREDITS[type],
    })),
  ];
};
